import { forwardRef, useId } from "react";

// Custom AI nav icon — not part of iconsax-react, pasted in from an external
// set (Untitled-UI/Solar-style robot face). Wrapped to accept the same
// size/color/variant prop interface iconsax-react icons use, since LeftNav
// renders every rail icon through that interface (see LeftNav.jsx).
//
// Deliberately wrapped in forwardRef: LeftNav's render branch picks between
// the iconsax-style props (size/color/variant) and the MUI-style sx prop
// based on `typeof item.Icon === "function"`. A plain arrow-function
// component is typeof "function" and would wrongly take the sx branch (which
// this component doesn't support); iconsax-react's own icons are all
// forwardRef-wrapped and come through as typeof "object", so matching that
// shape routes this through the size/color/variant branch it's built for.
//
// Bold (selected) state: head + antenna ball fill solid white, and the eyes
// + smile are cut out as true transparent holes via an SVG mask (not drawn
// in any color) so the selected pill's own background shows through them.
const AiFaceIcon = forwardRef(({ size = 24, color = "currentColor", variant = "Linear", ...rest }, ref) => {
  const isBold = variant === "Bold";
  const maskId = `ai-face-cutout-${useId()}`;

  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      color={color}
      fill="none"
      stroke={isBold ? "#fff" : color}
      strokeWidth={isBold ? 2 : 1.5}
      style={{ transform: "scale(1.15)" }} // sized as a % of its parent box like the other rail icons, nudged up slightly since it reads small next to them
      {...rest}
    >
      {isBold && (
        <defs>
          <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="24" height="24">
            <rect x="0" y="0" width="24" height="24" fill="#fff" />
            <path d="M9 13V14" stroke="#000" strokeWidth={2} strokeLinecap="round" />
            <path d="M15 13V14" stroke="#000" strokeWidth={2} strokeLinecap="round" />
            <path d="M10 17.5C10 17.5 10.6667 18 12 18C13.3333 18 14 17.5 14 17.5" stroke="#000" strokeWidth={2} strokeLinecap="round" />
          </mask>
        </defs>
      )}
      <path
        d="M19 16V14C19 11.1716 19 9.75736 18.1213 8.87868C17.2426 8 15.8284 8 13 8H11C8.17157 8 6.75736 8 5.87868 8.87868C5 9.75736 5 11.1716 5 14V16C5 18.8284 5 20.2426 5.87868 21.1213C6.75736 22 8.17157 22 11 22H13C15.8284 22 17.2426 22 18.1213 21.1213C19 20.2426 19 18.8284 19 16Z"
        strokeLinejoin="round"
        fill={isBold ? "#fff" : "none"}
        mask={isBold ? `url(#${maskId})` : undefined}
      />
      <path
        d="M19 18C20.4142 18 21.1213 18 21.5607 17.5607C22 17.1213 22 16.4142 22 15C22 13.5858 22 12.8787 21.5607 12.4393C21.1213 12 20.4142 12 19 12"
        strokeLinejoin="round"
      />
      <path
        d="M5 18C3.58579 18 2.87868 18 2.43934 17.5607C2 17.1213 2 16.4142 2 15C2 13.5858 2 12.8787 2.43934 12.4393C2.87868 12 3.58579 12 5 12"
        strokeLinejoin="round"
      />
      <path
        d="M13.5 3.5C13.5 4.32843 12.8284 5 12 5C11.1716 5 10.5 4.32843 10.5 3.5C10.5 2.67157 11.1716 2 12 2C12.8284 2 13.5 2.67157 13.5 3.5Z"
        fill={isBold ? "#fff" : "none"}
      />
      <path d="M12 5V8" strokeLinecap="round" strokeLinejoin="round" />
      {!isBold && (
        <>
          <path d="M9 13V14" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M15 13V14" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10 17.5C10 17.5 10.6667 18 12 18C13.3333 18 14 17.5 14 17.5" strokeLinecap="round" />
        </>
      )}
    </svg>
  );
});

export default AiFaceIcon;
